import CallSplitIcon from "@mui/icons-material/CallSplit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { type ChatRecord } from "@/shared/domain/chat";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./ChatFeedback.module.css";

interface ForkChatButtonProps {
  chatId: string;
  /** Org the source chat belongs to. Passed through as ``X-Roboto-Org-Id``
   *  so the backend's ``REQUIRES_CALLER_ORG`` check can pick an org for
   *  callers who belong to multiple orgs (admins). */
  chatOrgId: string;
  /** Sequence number of the message (inclusive) to fork at. Typically the
   *  sequence number of the assistant message the button is rendered next to. */
  messageSequenceNum: number;
  /** Disable while an upstream caller knows the message isn't a valid fork point. */
  disabled?: boolean;
}

/**
 * Trigger a fork of this chat at the given message.
 *
 * The fork endpoint is available to the chat's owner (forking their own
 * chat) and to Roboto admins (forking anyone's). The new chat is owned by
 * the caller and navigated to on success.
 */
export const ForkChatButton: React.FC<ForkChatButtonProps> = ({
  chatId,
  chatOrgId,
  messageSequenceNum,
  disabled,
}) => {
  const { chatService } = useDomainServices();
  const { handleHistoryChatClicked } = useAIChat();
  const queryClient = useQueryClient();

  const fork = useMutation<ChatRecord, Error, void>({
    mutationFn: () =>
      chatService.forkChat(chatId, messageSequenceNum, { orgId: chatOrgId }),
    onSuccess: async (forked) => {
      // Invalidate every cached chat-list/history query so the new fork
      // appears alongside the user's other chats. Without this the sidebar
      // history pane shows the new chat only on the next manual refresh.
      await queryClient.invalidateQueries({ queryKey: ["chats"] });
      // Swap the sidebar to the new chat in place; no URL routing because
      // the chat sidebar is an overlay, not a page.
      handleHistoryChatClicked(forked.session_id);
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error, {
        context: { chatId, messageSequenceNum },
      });
    },
  });

  const isDisabled = disabled || fork.isPending;
  // Distinguish "the parent told me to wait for the response to finish"
  // from "I'm currently forking" — the previous tooltip claimed the former
  // for both cases, which was visibly wrong while the spinner was up.
  let tooltip: string;
  if (fork.isPending) {
    tooltip = "Forking…";
  } else if (disabled) {
    tooltip = "Wait for the response to finish";
  } else {
    tooltip = "Fork this chat from here";
  }

  return (
    <RobotoTooltip title={tooltip}>
      <span>
        <RobotoIconButton
          eventName="ChatForkClicked"
          eventProperties={{ chatId, messageSequenceNum }}
          onClick={() => fork.mutate()}
          className={styles.iconButton}
          disabled={isDisabled}
        >
          <CallSplitIcon className={styles.actionIcon} />
        </RobotoIconButton>
      </span>
    </RobotoTooltip>
  );
};
